import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as userActions from '../../store/actions/user';
import ErrorMessage from '../../components/ErrorMessage';
import { DocumentPhotoIcon, InstantReviewIcon, LivenessCheckIcon, MailIcon, VerificationShieldIcon } from '../../assets/icons';

const REQUEST_COOLDOWN_MS = 5 * 60 * 1000;
const STORAGE_KEY = 'verification-pending-request-until';
const VERIFF_URL = 'https://www.veriff.com/';

function IndividualVerificationPending() {
  const dispatch = useDispatch();
  const { kycStatus, getKycStatusLoading, getKycStatusError, requestKycLoading, requestKycError } = useSelector((state) => state.user);
  const hasSuccessfulKyc = Array.isArray(kycStatus) && kycStatus.some((record) => record.isSuccessful === true);
  const [requestDisabledUntil, setRequestDisabledUntil] = useState(null);
  const [showVerificationLinkSent, setShowVerificationLinkSent] = useState(false);
  const wasRequestingRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    dispatch(userActions.getKycStatus());
  }, [dispatch]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const until = parseInt(stored, 10);
      if (until > Date.now()) {
        setRequestDisabledUntil(until);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  useEffect(() => {
    if (requestKycLoading) {
      wasRequestingRef.current = true;
    } else if (wasRequestingRef.current && !requestKycError && requestDisabledUntil != null) {
      setShowVerificationLinkSent(true);
      wasRequestingRef.current = false;
    } else if (!requestKycLoading) {
      wasRequestingRef.current = false;
    }
  }, [requestKycLoading, requestKycError, requestDisabledUntil]);

  useEffect(() => {
    if (requestDisabledUntil == null || requestDisabledUntil <= Date.now()) {
      return () => {};
    }
    const remaining = requestDisabledUntil - Date.now();
    timerRef.current = setTimeout(() => {
      setRequestDisabledUntil(null);
      localStorage.removeItem(STORAGE_KEY);
    }, remaining);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [requestDisabledUntil]);

  const handleRequestVerification = () => {
    setShowVerificationLinkSent(false);
    dispatch(userActions.requestKyc());
    const until = Date.now() + REQUEST_COOLDOWN_MS;
    setRequestDisabledUntil(until);
    localStorage.setItem(STORAGE_KEY, String(until));
  };

  const isRequestDisabled = hasSuccessfulKyc || requestKycLoading || (requestDisabledUntil != null && Date.now() < requestDisabledUntil);
  const displayError = requestKycError?.message ?? requestKycError?.detail ?? getKycStatusError?.message ?? getKycStatusError?.detail;
  const minutesLeft = requestDisabledUntil != null && requestDisabledUntil > Date.now() ? Math.ceil((requestDisabledUntil - Date.now()) / 60000) : 0;

  let buttonLabel = 'Request verification';
  if (hasSuccessfulKyc) {
    buttonLabel = 'Verification completed';
  } else if (requestKycLoading) {
    buttonLabel = 'Requesting...';
  } else if (isRequestDisabled && minutesLeft > 0) {
    buttonLabel = `Try again in ${minutesLeft} min`;
  }

  return (
    <div className='flex items-start justify-center bg-[#EBF4FB] px-6 pb-[120px] pt-9 sm:min-h-[60vh]'>
      <div className='w-full max-w-[540px] rounded-[20px] bg-white px-6 py-7 shadow-[0_4px_32px_rgba(11,25,41,0.08)] sm:px-12 sm:py-11'>
        <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#1E4E80]'>
          <VerificationShieldIcon width={28} height={28} aria-hidden />
        </div>
        <h1 className='mb-2.5 font-serif text-2xl font-normal leading-[1.25] text-[#0B1929]'>
          Let&apos;s verify your identity.
        </h1>
        <p className='mb-8 mt-2 text-sm leading-6 text-[#4E6F8A]'>Takes 2–3 minutes. Have your ID ready.</p>

        <div className='mb-6 overflow-hidden rounded-2xl border-[1.5px] border-[#DDE6EF] bg-white'>
          <div className='flex items-center gap-3.5 border-b border-[#EEF3F8] px-5 py-4'>
            <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#163659]'>
              <DocumentPhotoIcon width={20} height={20} aria-hidden />
            </div>
            <div>
              <div className='text-sm font-semibold text-[#0B1929]'>Document photo</div>
              <div className='mt-0.5 text-[13px] text-[#7A9AB0]'>Passport or national ID</div>
            </div>
          </div>

          <div className='flex items-center gap-3.5 border-b border-[#EEF3F8] px-5 py-4'>
            <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#163659]'>
              <LivenessCheckIcon width={20} height={20} aria-hidden />
            </div>
            <div>
              <div className='text-sm font-semibold text-[#0B1929]'>Liveness check</div>
              <div className='mt-0.5 text-[13px] text-[#7A9AB0]'>A brief selfie — confirms it is you</div>
            </div>
          </div>

          <div className='flex items-center gap-3.5 px-5 py-4'>
            <div className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-[#EBF4FB] text-[#163659]'>
              <InstantReviewIcon width={20} height={20} aria-hidden />
            </div>
            <div>
              <div className='text-sm font-semibold text-[#0B1929]'>Instant review</div>
              <div className='mt-0.5 text-[13px] text-[#7A9AB0]'>Automated — usually done in seconds</div>
            </div>
          </div>
        </div>

        {(hasSuccessfulKyc || (showVerificationLinkSent && !requestKycError)) && (
          <div className='mb-4 rounded-[10px] bg-[#dcfce7] px-[18px] py-3.5'>
            <div className='flex items-center gap-2.5 text-[#166534]'>
              <MailIcon className='h-[18px] w-[18px]' strokeWidth={2.25} aria-hidden />
              <span className='font-medium'>{hasSuccessfulKyc ? 'Identity verified. Continuing…' : 'Verification link sent. Check your inbox.'}</span>
            </div>
          </div>
        )}

        {getKycStatusLoading && <p className='mb-4 text-center text-sm text-[#7A9AB0]'>Loading status...</p>}
        {displayError && <ErrorMessage error={displayError} />}

        <p className='mb-3.5 text-center text-xs text-[#7A9AB0]'>
          Secured by{' '}
          <a href={VERIFF_URL} target='_blank' rel='noopener noreferrer' className='font-medium text-[#7A9AB0] hover:underline'>
            Veriff
          </a>{' '}
          · bank-grade identity verification
        </p>

        <button
          type='button'
          onClick={handleRequestVerification}
          disabled={isRequestDisabled}
          className='inline-flex w-full items-center justify-center gap-2 rounded-full border-0 bg-[#0B1929] px-7 py-[13px] text-center text-sm font-semibold text-white no-underline transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]'
        >
          {buttonLabel === 'Request verification' ? 'Start verification →' : buttonLabel}
        </button>
      </div>
    </div>
  );
}

export default IndividualVerificationPending;
